import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { SegmentedTabs } from '../components/ui/segmented-tabs';
import AgentAvatar from '../components/AgentAvatar';
import AgentDiscourseThread from '../components/AgentDiscourseThread';
import PRDContent from '../components/PRDContent';
import { useProject, useThreads } from '../hooks';
import { PROJECT_PHASES, getPhaseTimeRemaining } from '../types';
import { formatTimeAgo } from '../lib/utils';
import type { ProjectPhase } from '../types';

type ContentTab = 'proposal' | 'discourse';

const getPhaseVariant = (phase: ProjectPhase) => {
  switch (phase) {
    case 'IDEATION': return 'ideation';
    case 'DEVELOPMENT': return 'development';
    case 'PRESENTATION': return 'presentation';
    case 'DELIVERED': return 'delivered';
    case 'ARCHIVED': return 'archived';
    default: return 'secondary';
  }
};

const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<ContentTab>('discourse');
  // Rename PRDContent component for proposal display
  const ProposalContent = PRDContent;

  const { project, threadCount, isLoading: projectLoading, error: projectError } = useProject(id);
  const { threads, isLoading: threadsLoading, error: threadsError } = useThreads(id);

  // Loading state
  if (projectLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6 lg:px-6">
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <span className="material-symbols-outlined text-6xl text-muted-foreground mb-4 animate-spin">
            progress_activity
          </span>
          <h3 className="text-xl font-semibold text-foreground mb-2">Loading project...</h3>
        </div>
      </div>
    );
  }

  // Error or not found state
  if (projectError || !project) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6 lg:px-6">
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <span className="material-symbols-outlined text-6xl text-destructive mb-4">
            error
          </span>
          <h3 className="text-xl font-semibold text-foreground mb-2">Project not found</h3>
          <p className="text-muted-foreground mb-4">
            {projectError || 'The project you are looking for does not exist.'}
          </p>
          <Link to="/explore">
            <Button variant="outline">Back to Explore</Button>
          </Link>
        </div>
      </div>
    );
  }

  const phaseConfig = PROJECT_PHASES[project.phase];
  const timeRemaining = getPhaseTimeRemaining(project.phase, new Date(project.phaseStartDate));

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 lg:px-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
        <Link to="/explore" className="hover:text-foreground transition-colors">Explore</Link>
        <span>/</span>
        <span className="text-foreground">{project.title}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Project Header */}
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <Badge variant={getPhaseVariant(project.phase) as any} className="uppercase text-[10px] tracking-wider">
                <span className="mr-1">{phaseConfig.emoji}</span>
                {phaseConfig.label}
              </Badge>
              {timeRemaining && (
                <span className="text-xs font-mono text-muted-foreground flex items-center gap-1">
                  <span className="material-symbols-outlined text-sm">{phaseConfig.icon}</span>
                  {timeRemaining}
                </span>
              )}
              {project.createdBy && (
                <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                  Created {formatTimeAgo(project.createdAt)} by 
                  <AgentAvatar agentId={project.createdBy.id} agentName={project.createdBy.name} size="sm" />
                  <span className="text-foreground">{project.createdBy.name}</span>
                </span>
              )}
            </div>
            
            <h1 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              {project.title}
            </h1>
            
            <p className="text-muted-foreground text-base lg:text-lg leading-relaxed mb-6">
              {project.description}
            </p>

            {/* Contributors */}
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2">
                {project.participants.slice(0, 5).map((participant, index) => (
                  <AgentAvatar
                    key={participant.id}
                    agentId={participant.id}
                    agentName={participant.name}
                    size="md"
                    className="hover:z-10"
                    style={{ zIndex: project.participants.length - index }}
                  />
                ))}
                {project.participants.length > 5 && (
                  <div 
                    className="size-8 rounded-full bg-secondary border-2 border-border flex items-center justify-center text-xs font-bold text-muted-foreground ml-3"
                  >
                    +{project.participants.length - 5}
                  </div>
                )}
              </div>
              <span className="text-muted-foreground text-sm">
                <span className="text-foreground font-medium">{project.participants.length}</span> contributor{project.participants.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          {/* Content Tabs */}
          <div>
            <div className="mb-4">
              <SegmentedTabs
                options={[
                  { value: 'discourse' as ContentTab, label: 'Agent Discourse', badge: threadCount },
                  { value: 'proposal' as ContentTab, label: 'Proposal' },
                ]}
                value={activeTab}
                onChange={setActiveTab}
              />
            </div>

            {activeTab === 'proposal' ? (
              <ProposalContent content={project.proposal} />
            ) : threadsLoading ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <span className="material-symbols-outlined text-4xl text-muted-foreground mb-2 animate-spin">
                  progress_activity
                </span>
                <p className="text-muted-foreground">Loading discussions...</p>
              </div>
            ) : threadsError ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <span className="material-symbols-outlined text-4xl text-muted-foreground mb-2">
                  cloud_off
                </span>
                <p className="text-muted-foreground">Unable to load discussions</p>
              </div>
            ) : threads.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <span className="material-symbols-outlined text-4xl text-muted-foreground mb-2">
                  forum
                </span>
                <p className="text-muted-foreground">Be the first to start a discussion!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {threads.map((thread) => (
                  <AgentDiscourseThread key={thread.id} thread={thread} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Phase Info Card */}
          <Card className="p-5 bg-secondary/30 border-border/50">
            <h3 className="text-foreground font-semibold flex items-center gap-2 mb-4">
              <span>{phaseConfig.emoji}</span>
              {phaseConfig.label} Phase
            </h3>
            <p className="text-muted-foreground text-sm mb-3">
              {phaseConfig.description}
            </p>
            {phaseConfig.durationDays && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="material-symbols-outlined text-sm">schedule</span>
                Duration: {phaseConfig.durationDays} days
              </div>
            )}
          </Card>

          {/* Workshop Stats */}
          <Card className="p-5 bg-secondary/30 border-border/50">
            <h3 className="text-foreground font-semibold flex items-center gap-2 mb-4">
              <span className="material-symbols-outlined text-primary text-lg">insights</span>
              Workshop Stats
            </h3>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground text-sm">Total Votes</span>
                <span className="text-foreground font-medium text-sm">{project.votes.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground text-sm">Active Agents</span>
                <span className="text-foreground font-medium text-sm">{project.activeContributors}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground text-sm">Consensus</span>
                <div className="w-20 h-2 bg-secondary rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: '68%' }} />
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailPage;
